import React, { useCallback, useEffect, useState } from 'react';
import { researchosService } from '../../services/researchosService';

// Superuser-only: backfill ONE X handle through the official API (wish #6) — the X twin of BackfillPanel.
// Flow is Preview → explicit Start, like YouTube: Preview costs ONE user read ($0.01) and shows the account's
// lifetime post count + what we already hold; Start spends $0.005 per kept post (originals + quotes only —
// replies and plain reposts are never fetched or billed). The post cap is a hard cost ceiling.
const GOLD = 'rgb(var(--brand-gold-rgb))';
const PRICE = 0.005;

function fmtTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

const statusPill = (st) => ({
  queued: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  running: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  success: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  stopped: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
}[st] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300');

export default function XBackfillPanel() {
  const [handle, setHandle] = useState('');
  const [years, setYears] = useState('2');
  const [limit, setLimit] = useState('');     // most-recent N kept posts; blank = whole window
  const [quoted, setQuoted] = useState(true); // also fetch the TEXT of quoted posts (+$0.005 per quote); images are free
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [runs, setRuns] = useState([]);
  const [openRun, setOpenRun] = useState(null);
  const [preview, setPreview] = useState(null);   // null | 'loading' | preview dict | {error}
  const [usage, setUsage] = useState(null);       // X's own post-read counter for the billing cycle
  useEffect(() => {
    researchosService.getXUsage().then((r) => setUsage(r.data)).catch((e) => setUsage({ error: e?.response?.data?.error || 'unavailable' }));
  }, [runs.length]);

  const loadRuns = useCallback(async () => {
    try { setRuns((await researchosService.getBackfillRuns('x_backfill')).data || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => {
    const active = runs.some((r) => r.status === 'running' || r.status === 'queued');
    if (!active) return undefined;
    const t = setInterval(loadRuns, 6000);
    return () => clearInterval(t);
  }, [runs, loadRuns]);

  const clean = handle.trim().replace(/^@/, '');

  const runPreview = async () => {
    if (!clean) { setMsg('Handle required.'); return; }
    setPreview('loading'); setMsg('');
    try {
      setPreview((await researchosService.getXPreview(clean)).data);
    } catch (e) {
      setPreview({ error: e?.response?.data?.error || 'preview failed' });
    }
  };

  const submit = async () => {
    if (!clean) { setMsg('Handle required.'); return; }
    setBusy(true); setMsg('');
    try {
      await researchosService.triggerXBackfill({ handle: clean, years, limit: limit ? parseInt(limit, 10) : undefined, include_quoted: quoted });
      setPreview(null);
      setMsg('Backfill started — the run below shows posts fetched and the running cost.');
      setHandle(''); setLimit('');
      setTimeout(loadRuns, 1200);
    } catch (e) {
      setMsg('Failed to start: ' + (e?.response?.data?.error || e.message));
    } finally { setBusy(false); }
  };

  const stopRun = async (r) => {
    if (!window.confirm(`Stop the ${r.stats?.channel || ''} backfill? Days already stored are kept; a retry skips them.`)) return;
    try { await researchosService.stopBackfillRun(r.id); } catch { /* list refresh shows truth */ }
    loadRuns();
  };

  const retryFrom = (r) => {
    const s = r.stats || {};
    setHandle(s.handle || '');
    setYears(['1', '2', '3'].includes(String(s.years)) ? String(s.years) : 'all');
    setLimit(s.limit != null ? String(s.limit) : '');
    setMsg(`Form pre-filled from run #${r.id} — days already stored are skipped, so this resumes rather than re-buys.`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const spent = runs.reduce((a, r) => a + (Number(r.stats?.est_cost_usd) || 0), 0);
  const capCost = limit ? (parseInt(limit, 10) || 0) * PRICE + 0.01 : null;

  return (
    <div>
      <div className="mb-5 rounded-lg border border-gray-200 p-4 dark:border-gray-800">
        <div className="mb-3 text-[11px] uppercase tracking-wider text-gray-500">Backfill a handle</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[200px] flex-1">
            <label className="text-xs text-gray-500">Handle</label>
            <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@dylan522p"
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <div className="w-28">
            <label className="text-xs text-gray-500">Window</label>
            <select value={years} onChange={(e) => setYears(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900">
              <option value="1">1 year</option>
              <option value="2">2 years</option>
              <option value="3">3 years</option>
              <option value="all">All time</option>
            </select>
          </div>
          <div className="w-36">
            <label className="text-xs text-gray-500">Max posts (cost cap)</label>
            <input type="number" min="10" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="e.g. 1000"
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700" />
          </div>
          <button onClick={runPreview} disabled={busy || preview === 'loading'}
            className="rounded px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            style={{ color: GOLD, border: `1px solid ${GOLD}` }}>
            {preview === 'loading' ? 'Looking up…' : 'Preview ($0.01)'}
          </button>
          {/* Preview is OPTIONAL (owner 2026-10-11: a mandatory preview wastes a cent on every retry) — Start
              directly; the backfill resolves the account itself. */}
          <button onClick={submit} disabled={busy || !clean}
            className="rounded px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            style={{ background: GOLD }}>
            {busy ? 'Starting…' : 'Start backfill'}
          </button>
        </div>
        <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={quoted} onChange={(e) => setQuoted(e.target.checked)} />
          Also fetch the text of posts the handle quotes (the thing being reacted to). Measured 2026-10-11: X did not count 210 quoted posts as reads, so this has been free in practice; X&apos;s docs do not promise it. Image URLs are always included, free.
        </label>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Kept: the handle&apos;s own posts and quote posts. Replies and plain reposts are never fetched or billed.
          Price ${PRICE} per kept post{capCost != null && <> · this cap costs at most <b>{money(capCost)}</b></>}.
        </p>
        {preview && preview !== 'loading' && (
          preview.error ? (
            <p className="mt-2 text-xs text-red-500">{preview.error}</p>
          ) : (
            <div className="mt-3 rounded border p-3 text-sm" style={{ borderColor: GOLD }}>
              <div className="tabular-nums text-gray-700 dark:text-gray-200">
                <b>@{preview.handle}</b> {preview.name && <span className="text-gray-500">· {preview.name}</span>}
                {' · '}we hold {preview.stored_posts} post{preview.stored_posts === 1 ? '' : 's'} over {preview.stored_days} day{preview.stored_days === 1 ? '' : 's'}
                {preview.protected && <span className="ml-2 text-red-500">protected account — posts not public</span>}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Account size, for context only: {(preview.tweet_count_lifetime || 0).toLocaleString()} posts of every kind (replies and reposts included) since {preview.account_created_at ? preview.account_created_at.slice(0, 10) : '—'}.
                {' '}<b>That is not what gets fetched.</b> Start fetches only this handle&apos;s own posts and quotes inside the {years === 'all' ? 'all-time' : `${years}-year`} window{limit ? `, at most ${limit}` : ''};
                replies and reposts are filtered out by X before anything is returned, and the bill is the kept count × ${PRICE}, shown live on the run.
                {!limit && ' Set a max-posts cap for a hard ceiling.'}
              </div>
              <span className="mt-2 inline-flex gap-2">
                <button onClick={submit} disabled={busy || preview.protected}
                  className="rounded px-3 py-1 text-xs font-medium text-white disabled:opacity-50" style={{ background: GOLD }}>
                  {busy ? 'Starting…' : `Start backfill (${years === 'all' ? 'all time' : `${years}y`}${limit ? `, cap ${limit}` : ''})`}
                </button>
                <button onClick={() => setPreview(null)} className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-500 dark:border-gray-700">Cancel</button>
              </span>
            </div>
          )
        )}
        {msg && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{msg}</p>}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        <span className="uppercase tracking-wider">X post reads this billing cycle (X&apos;s own counter):</span>
        {usage == null ? <span>loading…</span>
          : usage.error ? <span className="text-gray-400">{usage.error}</span>
          : <span className="tabular-nums text-gray-700 dark:text-gray-300">{(usage.post_reads_cycle || 0).toLocaleString()} posts ≈ <b>{money(usage.post_reads_usd)}</b></span>}
        <span className="text-gray-400">· plus $0.01 per account lookup · run rows show an estimated max ({money(spent)} summed) that cannot see X&apos;s same-day dedup · the console&apos;s billing page is final</span>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-800">
        <div className="border-b border-gray-100 px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 dark:border-gray-800/60">Recent runs</div>
        {runs.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">No runs yet.</p>
        ) : runs.map((r) => {
          const s = r.stats || {};
          const isOpen = openRun === r.id;
          const fails = s.failures || [];
          return (
            <div key={r.id} className="border-b border-gray-100 last:border-b-0 dark:border-gray-800/60">
              <button onClick={() => setOpenRun(isOpen ? null : r.id)}
                className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800/40">
                <span className={`rounded px-1.5 py-0.5 text-[10px] ${statusPill(r.status)}`}>{r.status}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{s.channel || `@${s.handle}`}</span>
                <span className="text-gray-500">win {s.since || (s.years && s.years !== 'all' ? `${s.years}y` : 'all')}{s.limit ? ` · cap ${s.limit}` : ''}{s.include_quoted ? ' · +quoted' : ''}</span>
                <span className="tabular-nums text-gray-600 dark:text-gray-300">{s.posts_fetched ?? 0} fetched</span>
                <span className="tabular-nums text-gray-600 dark:text-gray-300">{s.posts_kept ?? 0} kept</span>
                <span className="tabular-nums text-green-600 dark:text-green-400">{s.new ?? 0} new days</span>
                {(s.refreshed ?? 0) > 0 && <span className="tabular-nums" style={{ color: GOLD }}>{s.refreshed} refreshed{(s.refreshed_extracted ?? 0) > 0 ? ` (${s.refreshed_extracted} need re-extraction → Extraction tab, redo)` : ''}</span>}
                <span className="tabular-nums text-gray-500">{s.had ?? 0} had</span>
                <span className={`tabular-nums ${(s.failed ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{s.failed ?? 0} failed</span>
                <span className="tabular-nums" style={{ color: GOLD }}>{money(s.est_cost_usd)}</span>
                <span className="ml-auto flex items-center gap-3">
                  {(r.status === 'running' || r.status === 'queued') && (
                    <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); stopRun(r); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); stopRun(r); } }}
                      className="text-xs text-red-500 hover:underline">⏹ stop</span>
                  )}
                  {(r.status === 'failed' || r.status === 'success' || r.status === 'stopped') && s.handle && (
                    <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); retryFrom(r); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); retryFrom(r); } }}
                      className="text-xs hover:underline" style={{ color: GOLD }}>↻ retry</span>
                  )}
                  <span className="text-xs text-gray-400">{fmtTime(r.started_at)}</span>
                </span>
              </button>
              {isOpen && (
                <div className="bg-gray-50 px-4 py-2 text-xs text-gray-600 dark:bg-gray-800/30 dark:text-gray-400">
                  <div className="tabular-nums">
                    {s.requests ?? 0} search request{s.requests === 1 ? '' : 's'} · {s.user_reads ?? 0} user read{(s.quoted_fetched ?? 0) > 0 ? ` · ${s.quoted_fetched} quoted posts` : ''} · posts {s.oldest_post_at ? s.oldest_post_at.slice(0, 10) : '—'} → {s.newest_post_at ? s.newest_post_at.slice(0, 10) : '—'}
                    {s.tweet_count_lifetime != null && ` · account lifetime ${Number(s.tweet_count_lifetime).toLocaleString()} posts (all kinds)`}
                  </div>
                  {fails.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {fails.map((f, i) => (
                        <li key={i}><span className="font-mono text-gray-500">{f.id}</span> {f.title} — <span className="text-red-500">{f.error}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
